import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useRouteLoaderData } from '@remix-run/react';

import { COMMUNITY_ROUTES } from '~/constants/community';
import type { RootLoaderData } from '~/root';
import logout from '~/utils/logout';

import type { ActionMenuItem } from '../_components/ui/ActionMenu';

import { useCommunityMembership } from './useCommunityMembership';

const getDisplayName = (firstName?: string | null, lastName?: string | null): string => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
  return fullName || 'Guest';
};

interface MemberMenu {
  isLoggedIn: boolean;
  displayName: string;
  email: string | null;
  subscriberId: string | null;
  avatarUrl: string | null;
  menuItems: ActionMenuItem[];
}

export const useMemberMenu = (onNavigate?: () => void): MemberMenu => {
  const { t } = useTranslation();
  const rootData = useRouteLoaderData('root') as RootLoaderData | undefined;
  const { currentMember } = useCommunityMembership();

  const isLoggedIn = Boolean(rootData?.isLoggedIn);
  const subscription = rootData?.subscription;

  const profile = useMemo(() => {
    if (currentMember?.subscription) {
      return {
        firstName: currentMember.subscription.first_name,
        lastName: currentMember.subscription.last_name,
        email: currentMember.subscription.email,
        subscriberId: currentMember.subscription.subscriber_id,
        avatarUrl: currentMember.subscription.avatar_url,
      };
    }

    return {
      firstName: subscription?.first_name ?? null,
      lastName: subscription?.last_name ?? null,
      email: subscription?.email ?? null,
      subscriberId: subscription?.subscriber_id ?? null,
      avatarUrl: subscription?.avatar_url ?? null,
    };
  }, [
    currentMember?.subscription,
    subscription?.avatar_url,
    subscription?.email,
    subscription?.first_name,
    subscription?.last_name,
    subscription?.subscriber_id,
  ]);

  const menuItems = useMemo<ActionMenuItem[]>(() => {
    const logoutItem: ActionMenuItem = {
      key: 'logout',
      label: t('shared.cta.logout'),
      onClick: () => logout('token'),
    };

    if (!currentMember) return [logoutItem];

    return [
      {
        key: 'view_profile',
        label: t('community.profile.view_profile'),
        to: COMMUNITY_ROUTES.member(currentMember.id),
        onClick: onNavigate,
      },
      {
        key: 'settings',
        label: t('community.settings.title'),
        to: COMMUNITY_ROUTES.memberSettings,
        onClick: onNavigate,
      },
      { type: 'divider', key: 'settings_logout_divider' } as ActionMenuItem,
      logoutItem,
    ];
  }, [currentMember, onNavigate, t]);

  return {
    isLoggedIn,
    displayName: getDisplayName(profile.firstName, profile.lastName),
    email: profile.email || null,
    subscriberId: profile.subscriberId || null,
    avatarUrl: profile.avatarUrl ?? null,
    menuItems,
  };
};
